import { createFileRoute } from "@tanstack/react-router"

type EventLogSearch = {
  streamId?: number
  eventId?: string
}

export const Route = createFileRoute("/app/duties/leave-requests/event-log")({
  component: EventLogPage,
  validateSearch: (search: Record<string, unknown>): EventLogSearch => {
    return {
      streamId:
        typeof search.streamId === "number" ? search.streamId : undefined,
      eventId: typeof search.eventId === "string" ? search.eventId : undefined,
    }
  },
})

import { Group, Button, Flex, Box, ThemeIcon, Text } from "@mantine/core"
import { IconX } from "@tabler/icons-react"
import dayjs from "dayjs"
import { match } from "ts-pattern"
import { Property } from "../../../../-components/property"
import { PrincipalName } from "../../../../blocks/persons-and-users/User"
import { SimpleTable } from "../../../../blocks/tables/simple-table"
import { useSuspenseQueryGet } from "../../../../typed-client/hooks/use-suspense-query-get"
import { Debug } from "../../../../utils/debug"

function EventLogPage() {
  const events = useSuspenseQueryGet("/api/AbsencesEventLog/GetAllEvents", null)

  const navigate = Route.useNavigate()
  const x = Route.useSearch()

  const elements = x.streamId
    ? events.data.filter((v) => v.streamId === x.streamId)
    : events.data
  const selectedEvent = x.eventId
    ? events.data.find((v) => v.eventId === x.eventId) || null
    : null
  return (
    <div style={{ width: "100%" }}>
      <Group>
        <Button
          size="xs"
          variant="default"
          onClick={() => navigate({ to: ".", search: { streamId: undefined } })}
        >
          All
        </Button>
        <Button size="xs" variant="default" onClick={() => events.refetch()}>
          Refresh
        </Button>
      </Group>
      <Flex align={"start"}>
        <SimpleTable
          data={elements}
          header={[
            "Stream id",
            "Event id",
            "Version",
            "Name",
            "User",
            "Timestamp",
          ]}
          renderRow={(v) => [
            <div>
              <Button
                size="xs"
                variant="subtle"
                onClick={() =>
                  navigate({ to: ".", search: { streamId: v.streamId } })
                }
              >
                {v.streamId}
              </Button>
            </div>,
            <Button
              size="xs"
              variant="subtle"
              onClick={() =>
                navigate({
                  to: ".",
                  search: { eventId: v.eventId, streamId: x.streamId },
                })
              }
            >
              <Box w={60}>
                <Text truncate="end">{v.eventId}</Text>
              </Box>
            </Button>,
            v.version,
            match(v.payload)
              .with(
                { Case: "WorkflowEvent" },
                (x) =>
                  "WF/" +
                  match(x.Fields)
                    .with(
                      { Case: "NodeEvent" },
                      (v) => "Node/" + v.Fields.item1.Case
                    )
                    .with({ Case: "WorkflowEvent" }, (v) => v.Fields.Case)
                    .exhaustive()
              )
              .otherwise((v) => v.Case),
            <PrincipalName id={v.header.principalId} />,
            dayjs(v.timestamp).format("L LT"),
          ]}
        />
        {selectedEvent ? (
          <Box miw={400} p="xs">
            <ThemeIcon
              variant="transparent"
              color="dark"
              size="xs"
              onClick={() => {
                navigate({
                  to: ".",
                  search: { streamId: x.streamId },
                })
              }}
            >
              <IconX />
            </ThemeIcon>
            <Text fw={700}>{selectedEvent.payload.Case}</Text>
            <Property label="Timestamp">
              {dayjs(selectedEvent.timestamp).format("L LT")}
            </Property>
            <Property label="User">
              <PrincipalName id={selectedEvent.header.principalId} />
            </Property>
            <Property label="Version">{selectedEvent.version}</Property>
            {/* TODO: Check this. Added as any. Remove it and fix the issue */}
            <Debug _show {...selectedEvent.payload} />
          </Box>
        ) : null}
        {x.streamId ? (
          <Box miw={400} p="xs">
            <ThemeIcon
              variant="transparent"
              color="dark"
              size="xs"
              onClick={() => {
                navigate({
                  to: ".",
                  search: { eventId: x.eventId },
                })
              }}
            >
              <IconX />
            </ThemeIcon>
            <StreamState streamId={x.streamId} />{" "}
          </Box>
        ) : null}
      </Flex>
      <Text c="dimmed" size="xs" mt="xl">
        Last update: {dayjs(events.dataUpdatedAt).format("LTS")}
      </Text>
    </div>
  )
}

function StreamState({ streamId }: { streamId: number }) {
  const data = useSuspenseQueryGet("/api/AbsencesEventLog/GetStream", {
    streamId,
  })
  return <Debug _show {...data.data} />
}
